import { useEffect, useRef } from "react";

export function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  if (typeof ctx.roundRect === "function") {
    ctx.roundRect(x, y, w, h, r);
  } else {
    const rad = Math.min(r, w / 2, h / 2);
    ctx.moveTo(x + rad, y);
    ctx.arcTo(x + w, y, x + w, y + h, rad);
    ctx.arcTo(x + w, y + h, x, y + h, rad);
    ctx.arcTo(x, y + h, x, y, rad);
    ctx.arcTo(x, y, x + w, y, rad);
    ctx.closePath();
  }
}

/**
 * Draw a tapered anatomical limb segment between (x1, y1) and (x2, y2)
 * using a tangent quad + two filled endpoint circles to prevent self-intersecting arcs.
 */
export function drawLimb(ctx, x1, y1, x2, y2, r1, r2, color) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  const nx = -dy / len;
  const ny = dx / len;

  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(x1 + nx * r1, y1 + ny * r1);
  ctx.lineTo(x2 + nx * r2, y2 + ny * r2);
  ctx.lineTo(x2 - nx * r2, y2 - ny * r2);
  ctx.lineTo(x1 - nx * r1, y1 - ny * r1);
  ctx.closePath();
  ctx.fill();

  ctx.beginPath();
  ctx.arc(x1, y1, r1, 0, Math.PI * 2);
  ctx.fill();

  ctx.beginPath();
  ctx.arc(x2, y2, r2, 0, Math.PI * 2);
  ctx.fill();
}

export function drawPersonHead(ctx, shoulderX, shoulderY, headX, headY, dir = 1) {
  drawLimb(ctx, shoulderX, shoulderY, headX, headY, 6.5, 6.5, "#F0C49E");
  ctx.fillStyle = "#F0C49E";
  ctx.beginPath();
  ctx.arc(headX, headY - 4, 15, 0, Math.PI * 2);
  ctx.fill();

  // Hair cap
  ctx.fillStyle = "#2D2A24";
  ctx.beginPath();
  if (dir >= 0) {
    ctx.arc(headX - 2, headY - 7, 15, Math.PI * 0.85, Math.PI * 2.05);
  } else {
    ctx.arc(headX + 2, headY - 7, 15, Math.PI * 0.95, Math.PI * 2.15);
  }
  ctx.fill();

  // Eye
  ctx.fillStyle = "#2D2A24";
  ctx.beginPath();
  ctx.arc(headX + dir * 8, headY - 5, 2, 0, Math.PI * 2);
  ctx.fill();
}

export function drawSneaker(ctx, ankleX, ankleY, color = "#00628F") {
  drawLimb(ctx, ankleX, ankleY - 12, ankleX, ankleY, 7, 7, "#FFFFFF");
  ctx.fillStyle = color;
  roundRect(ctx, ankleX - 9, ankleY - 4, 34, 13, 6);
  ctx.fill();
  ctx.fillStyle = "#FFFFFF";
  roundRect(ctx, ankleX - 9, ankleY + 5, 35, 4, 2);
  ctx.fill();
}

export function drawCardFrame(
  ctx,
  {
    title,
    subtitle,
    badgeText,
    progress = 0,
    isHighlight = false,
    accentColor = "#00628F",
    accentLight = "#E3F0F5",
    highlightColor = "#157346",
  }
) {
  ctx.clearRect(0, 0, 600, 420);

  // Outer card
  ctx.save();
  ctx.shadowColor = "rgba(45, 42, 36, 0.06)";
  ctx.shadowBlur = 20;
  ctx.shadowOffsetY = 6;
  roundRect(ctx, 12, 10, 576, 400, 24);
  ctx.fillStyle = "#FFFFFF";
  ctx.fill();
  ctx.restore();

  roundRect(ctx, 12, 10, 576, 400, 24);
  ctx.strokeStyle = "#E2D9CB";
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Soft radial glow in exercise stage
  const grad = ctx.createRadialGradient(215, 220, 10, 215, 220, 165);
  grad.addColorStop(
    0,
    isHighlight ? "rgba(21, 115, 70, 0.12)" : "rgba(0, 98, 143, 0.09)"
  );
  grad.addColorStop(1, "rgba(227, 240, 245, 0)");
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(215, 220, 165, 0, Math.PI * 2);
  ctx.fill();

  // Header bar inside card
  ctx.fillStyle = accentColor;
  ctx.font = '800 17px "Plus Jakarta Sans", -apple-system, sans-serif';
  ctx.fillText(title, 36, 46);

  ctx.fillStyle = "#71644C";
  ctx.font = '600 11.5px "Plus Jakarta Sans", -apple-system, sans-serif';
  ctx.fillText(subtitle, 36, 66);

  // Top-right stopwatch / status badge
  if (badgeText) {
    roundRect(ctx, 446, 24, 122, 38, 19);
    ctx.fillStyle = accentLight;
    ctx.fill();
    ctx.strokeStyle = accentColor;
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // Animated mini clock arc
    ctx.beginPath();
    ctx.arc(467, 43, 10, -Math.PI / 2, -Math.PI / 2 + progress * Math.PI * 2);
    ctx.strokeStyle = isHighlight ? highlightColor : accentColor;
    ctx.lineWidth = 2.5;
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(467, 43, 10, 0, Math.PI * 2);
    ctx.strokeStyle = "rgba(0,98,143,0.18)";
    ctx.lineWidth = 1.5;
    ctx.stroke();

    ctx.fillStyle = accentColor;
    ctx.font = '800 12px "Plus Jakarta Sans", -apple-system, sans-serif';
    ctx.fillText(badgeText, 484, 48);
  }
}

export function drawChecklistPanel(
  ctx,
  { title = "Técnica Correcta", tips = [], panelX = 374, panelY = 100 }
) {
  const panelH = 44 + tips.length * 50;
  roundRect(ctx, panelX, panelY, 194, panelH, 16);
  ctx.fillStyle = "#FCFAEE";
  ctx.fill();
  ctx.strokeStyle = "#E2D9CB";
  ctx.lineWidth = 1.2;
  ctx.stroke();

  ctx.fillStyle = "#2D2A24";
  ctx.font = '800 12px "Plus Jakarta Sans", -apple-system, sans-serif';
  ctx.fillText(title, panelX + 14, panelY + 24);

  tips.forEach((tip, i) => {
    const ty = panelY + 38 + i * 48;
    const isWarn = tip.warn;
    const active = tip.active;
    roundRect(ctx, panelX + 10, ty, 174, 41, 10);
    ctx.fillStyle = isWarn
      ? "#FCEAE8"
      : active
      ? "#E2F0E8"
      : "#FFFFFF";
    ctx.fill();
    ctx.strokeStyle = isWarn
      ? "#C0453A"
      : active
      ? "#157346"
      : "#E2D9CB";
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.fillStyle = isWarn
      ? "#C0453A"
      : active
      ? "#157346"
      : "#F9A01B";
    ctx.beginPath();
    ctx.arc(panelX + 24, ty + 20.5, 6, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = "#2D2A24";
    ctx.font = '700 11px "Plus Jakarta Sans", -apple-system, sans-serif';
    ctx.fillText(tip.label, panelX + 36, ty + 17);

    ctx.fillStyle = "#71644C";
    ctx.font = '500 9.5px "Plus Jakarta Sans", -apple-system, sans-serif';
    ctx.fillText(tip.sub, panelX + 36, ty + 31);
  });
}

export function drawPhasePill(
  ctx,
  {
    label,
    isHighlight = false,
    accentColor = "#00628F",
    accentLight = "#E3F0F5",
    highlightColor = "#157346",
    highlightLight = "#E2F0E8",
    rightBadgeText = null,
  }
) {
  roundRect(ctx, 36, 358, rightBadgeText ? 330 : 350, 32, 16);
  ctx.fillStyle = isHighlight ? highlightLight : accentLight;
  ctx.fill();
  ctx.fillStyle = isHighlight ? highlightColor : accentColor;
  ctx.font = '700 12px "Plus Jakarta Sans", -apple-system, sans-serif';
  ctx.fillText(label, 54, 378);

  if (rightBadgeText) {
    roundRect(ctx, 378, 358, 190, 32, 16);
    ctx.fillStyle = "#E2F0E8";
    ctx.fill();
    ctx.strokeStyle = "#157346";
    ctx.lineWidth = 1.2;
    ctx.stroke();
    ctx.fillStyle = "#157346";
    ctx.font = '800 12px "Plus Jakarta Sans", -apple-system, sans-serif';
    ctx.fillText(rightBadgeText, 396, 378);
  }
}

export function ExerciseCanvas({ renderFrame, cycleDurationMs = 2400, testId }) {
  const canvasRef = useRef(null);
  const renderRef = useRef(renderFrame);
  renderRef.current = renderFrame;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || typeof canvas.getContext !== "function") return;
    let ctx = null;
    try {
      ctx = canvas.getContext("2d");
    } catch {
      return;
    }
    if (!ctx) return;

    let animId = null;
    let startTime = null;

    const tick = (now) => {
      if (startTime === null) startTime = now;
      const elapsed = (now - startTime) % cycleDurationMs;
      const t = elapsed / cycleDurationMs;

      ctx.save();
      ctx.setTransform(2, 0, 0, 2, 0, 0); // 2x Retina supersampling (600x420 logical)
      renderRef.current(ctx, t);
      ctx.restore();

      animId = window.requestAnimationFrame(tick);
    };

    animId = window.requestAnimationFrame(tick);
    return () => {
      if (animId !== null && typeof window.cancelAnimationFrame === "function") {
        window.cancelAnimationFrame(animId);
      }
    };
  }, [cycleDurationMs]);

  return (
    <canvas
      ref={canvasRef}
      width={1200}
      height={840}
      data-testid={testId}
      style={{
        width: "100%",
        maxWidth: 540,
        aspectRatio: "600 / 420",
        display: "block",
        margin: "0 auto",
        borderRadius: 20,
      }}
    />
  );
}

export function ExerciseVideoCard({
  videoSrc,
  title,
  subtitle,
  badgeText,
  checklistTitle = "Técnica Correcta",
  tips = [],
  phaseLabel,
  rightBadgeText = null,
  isHighlight = false,
  testId,
}) {
  return (
    <div
      data-testid={testId}
      className="w-full max-w-[560px] mx-auto bg-white border border-[#E2D9CB] rounded-3xl p-4 sm:p-5 shadow-[0_8px_24px_rgba(45,42,36,0.06)] text-left"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h4 className="text-[16px] sm:text-[17px] font-extrabold text-[#00628F] leading-tight">
            {title}
          </h4>
          <p className="text-[11.5px] font-semibold text-[#71644C] mt-0.5">
            {subtitle}
          </p>
        </div>
        {badgeText && (
          <span className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11.5px] font-extrabold bg-[#E3F0F5] text-[#00628F] border border-[#00628F]/30">
            <span className="w-2 h-2 rounded-full bg-[#157346] animate-pulse" />
            {badgeText}
          </span>
        )}
      </div>

      {/* Full-Width Uncropped Video Stage (16:10 matching 768x480 videos) */}
      <div
        className="relative w-full aspect-[16/10] rounded-2xl overflow-hidden border border-[#E2D9CB] flex items-center justify-center mb-3"
        style={{
          background: "linear-gradient(180deg, #DEC7A9 0%, #E3D1B8 68%, #EAE3DA 100%)",
        }}
      >
        <video
          key={videoSrc}
          src={videoSrc}
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-contain block"
        />
        <span className="absolute top-2.5 left-2.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-[#2D2A24]/75 text-white backdrop-blur-sm pointer-events-none">
          Guía biomecánica WE360
        </span>
      </div>

      {/* Compact Horizontal Checklist Strip */}
      {tips.length > 0 && (
        <div className="bg-[#FCFAEE] border border-[#E2D9CB] rounded-2xl p-2.5">
          <div className="text-[11px] font-extrabold uppercase tracking-wider text-[#71644C] mb-2 px-1">
            {checklistTitle}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {tips.map((tip, idx) => {
              const isWarn = tip.warn;
              const active = tip.active;
              return (
                <div
                  key={idx}
                  className={`flex items-center gap-2 px-2.5 py-2 rounded-xl border text-left transition-colors ${
                    isWarn
                      ? "bg-[#FCEAE8] border-[#C0453A]"
                      : active
                      ? "bg-[#E2F0E8] border-[#157346]"
                      : "bg-white border-[#E2D9CB]"
                  }`}
                >
                  <span
                    className={`w-2 h-2 rounded-full shrink-0 ${
                      isWarn
                        ? "bg-[#C0453A]"
                        : active
                        ? "bg-[#157346]"
                        : "bg-[#F9A01B]"
                    }`}
                  />
                  <div className="min-w-0">
                    <div className="text-[11px] font-bold text-[#2D2A24] leading-tight truncate">
                      {tip.label}
                    </div>
                    <div className="text-[9.5px] font-medium text-[#71644C] leading-tight mt-0.5 truncate">
                      {tip.sub}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Bottom Phase Pill */}
      {phaseLabel && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <div
            className={`flex-1 min-w-[200px] px-3.5 py-1.5 rounded-full text-[11px] font-bold text-center ${
              isHighlight
                ? "bg-[#E2F0E8] text-[#157346]"
                : "bg-[#E3F0F5] text-[#00628F]"
            }`}
          >
            {phaseLabel}
          </div>
          {rightBadgeText && (
            <div className="px-3.5 py-1.5 rounded-full text-[11.5px] font-extrabold bg-[#E2F0E8] text-[#157346] border border-[#157346]">
              {rightBadgeText}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

